import { Component, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import gsap from 'gsap';
import { HeaderComponent } from './components/layout/header/header.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { AnalyticsService } from './core/services/analytics.service';
import { RedirectService } from './core/services/redirect.service';

export interface UTMParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  timestamp?: string;
}

/**
 * Analytics Service
 * Implements the tracking strategy defined in SEO_STRATEGY.md
 * 
 * @see context/SEO_STRATEGY.md for full strategy documentation
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
    imports: [CommonModule, RouterOutlet, HeaderComponent, FooterComponent],
    standalone: true
})
export class AppComponent implements OnInit {
  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    private analytics: AnalyticsService,
    private redirectService: RedirectService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.setupDefaultSEO();
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Check for legacy URLs on initial load
      this.checkForLegacyUrls();
      
      // Original initialization
      this.initializeAnimations();
      this.setupUtmTracking();
      this.injectStructuredData();
    }
  }

  private checkForLegacyUrls(): void {
    // Only run in browser environment
    if (isPlatformBrowser(this.platformId)) {
      const currentUrl = window.location.href;
      
      // Check if the current URL is a legacy URL
      if (currentUrl.includes('i2report/index.cfm')) {
        this.redirectService.handleLegacyUrl(currentUrl);
      }
    }
  }

  private setupUtmTracking(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams: Partial<UTMParams> = {};
      
      // Extract UTM parameters
      ['source', 'medium', 'campaign', 'term', 'content'].forEach(param => {
        const value = urlParams.get(`utm_${param}`);
        if (value) {
          utmParams[`utm_${param}` as keyof UTMParams] = value;
        }
      });

      // Track UTM parameters if any exist
      if (Object.keys(utmParams).length > 0) {
        this.analytics.trackUTM(utmParams);
        
        // Store UTM data in sessionStorage for cross-page tracking
        sessionStorage.setItem('utm_data', JSON.stringify({
          ...utmParams,
          landing_page: window.location.pathname,
          timestamp: new Date().toISOString()
        }));
      }
    });
  }

  private injectStructuredData(): void {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Industry Intelligence Inc.',
      description: 'Market intelligence platform for real-time data and competitive insights.',
      url: 'https://industryintel.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://industryintel.com/assets/images/logo/i2-logo.svg'
      },
      sameAs: [
        'https://twitter.com/industryintel',
        'https://www.linkedin.com/company/industry-intelligence',
        'https://www.youtube.com/industryintelligence'
      ]
    };

    const scriptElement = document.createElement('script');
    scriptElement.type = 'application/ld+json';
    scriptElement.text = JSON.stringify(structuredData);
    document.head.appendChild(scriptElement);
  }

  private setupDefaultSEO(): void {
    // Set basic meta tags
    this.title.setTitle('Industry Intelligence Inc.');
    this.meta.updateTag({ 
      name: 'description', 
      content: 'Industry Intelligence Inc. - Market intelligence platform for real-time data and competitive insights.'
    });

    // Set Open Graph tags
    this.meta.updateTag({ property: 'og:title', content: 'Industry Intelligence Inc.' });
    this.meta.updateTag({ property: 'og:description', content: 'Market intelligence platform for real-time data and competitive insights.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: 'https://industryintel.com' });
    this.meta.updateTag({ property: 'og:image', content: 'https://industryintel.com/assets/images/logo/i2-logo.svg' });

    // Set Twitter Card tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: 'Industry Intelligence Inc.' });
    this.meta.updateTag({ name: 'twitter:description', content: 'Market intelligence platform for real-time data and competitive insights.' });
    this.meta.updateTag({ name: 'twitter:image', content: 'https://industryintel.com/assets/images/logo/i2-logo.svg' });

    // Set structured data as meta tag for SSR
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Industry Intelligence Inc.',
      description: 'Market intelligence platform for real-time data and competitive insights.',
      url: 'https://industryintel.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://industryintel.com/assets/images/logo/i2-logo.svg'
      },
      sameAs: [
        'https://twitter.com/industryintel',
        'https://www.linkedin.com/company/industry-intelligence',
        'https://www.youtube.com/industryintelligence'
      ]
    };

    this.meta.addTag({ 
      name: 'structured-data',
      content: JSON.stringify(structuredData)
    });
  }

  private initializeAnimations(): void {
    // Initialize GSAP animations
    gsap.config({
      nullTargetWarn: false
    });
    
    // Add any global animations here
    gsap.from('app-root', {
      opacity: 0,
      duration: 0.5,
      ease: 'power2.out'
    });
  }
}
